import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes';
import { handleAuthenticatedNavigation } from '@/utils/routeGuards';
import { isAuthenticated } from '@/stores/authStore';
import { pageCustomerIo } from '@/plugins/customerio';
import { getFlag } from '@/plugins/launchdarkly';
import { FeatureFlags } from '@/types/featureFlags';

declare module 'vue-router' {
  interface RouteMeta {
    layout: 'LayoutAuthenticated' | 'LayoutDefault';
    layoutOptions?: Record<string, unknown>;
    title?: string;
    requiresAuth: boolean;
    requiredLDFlags?: string[];
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Drops the app-bar border on the agents area when the Agent Directory tab is
// visible (the tab bar provides its own divider). Runs in the global guard:
// query-only navigations (filters/search) reuse the route records and skip
// per-record guards, so a per-record beforeEnter would lose this.
function applyAgentDirectoryLayoutOptions(to: {
  name?: unknown;
  meta: { layoutOptions?: object };
}) {
  if (to.name === 'MyAgents' || to.name === 'AgentDirectory') {
    if (getFlag(FeatureFlags.AGENT_DIRECTORY_ENABLED, false)) {
      to.meta.layoutOptions = { noAppBarBorder: true };
    }
  }
}

router.beforeEach(async (to) => {
  // This means that we're using the back button to go back to the budget list, but due to the optionalTwrn, we're being directed to the individual budget page. However since there is no budget twrn, we're getting an error when trying to fetch a budget. However due to a Vue Router bug, we need to keep the param at optional level https://github.com/vuejs/router/issues/845
  if (to.path === '/budgets/roster-view' && !to.params.budgetTwrn) {
    return {
      name: 'TeamBudgets',
      query: { ...(to.query || {}) },
    };
  }
  // agreements too
  if (to.path === '/agreements/overview' && !to.params.agreementTwrn) {
    return {
      name: 'Agreements',
      query: { ...(to.query || {}) },
    };
  }

  if (import.meta.env.TEST) return;
  if (to.meta.requiresAuth) {
    const authResult = await handleAuthenticatedNavigation(to);
    if (authResult !== true) return authResult;
  }
  // After auth handling so the LD context is initialized when the flag reads.
  applyAgentDirectoryLayoutOptions(to);
  return true;
});

router.afterEach((to, from) => {
  if (from.name != null && to.path !== from.path) {
    document.getElementById('main-content')?.focus();
  }

  if (import.meta.env.TEST) return;
  if (!isAuthenticated()) return;
  if (to.path === from.path) return;
  pageCustomerIo(String(to.name ?? to.path), to.path);
});

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Failed to load module script')) {
    (window as Window).location = to.fullPath;
  }
});

export default router;
